/**
 * useInboxGroups - Hooks para gerenciar Grupos de Inbox
 *
 * Fornece query de listagem e mutations de CRUD via RPCs SECURITY DEFINER:
 *  - list_inbox_groups
 *  - manage_inbox_group (create/update/delete)
 *  - set_inbox_group (vincula/desvincula inbox)
 *
 * Tabela inbox_groups não tem SELECT direto pelo frontend — tudo via RPC.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-01 v1.0.0 - Criação inicial
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// =============================================================================
// Tipos
// =============================================================================

export interface InboxGroup {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
  inbox_count: number;
  created_at: string;
  updated_at: string;
}

export interface ManageInboxGroupPayload {
  action: 'create' | 'update' | 'delete';
  groupId?: string;
  name?: string;
  description?: string | null;
  color?: string;
  isActive?: boolean;
}

// =============================================================================
// Hook: listagem de grupos
// =============================================================================

export function useInboxGroups(onlyActive = false) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['inbox-groups', currentTenant?.id, onlyActive],
    queryFn: async (): Promise<InboxGroup[]> => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase.rpc('list_inbox_groups', {
        p_tenant_hint: currentTenant.id,
        p_only_active: onlyActive,
      });

      if (error) {
        logger.error('useInboxGroups', 'Erro ao listar grupos', { error: error.message });
        throw error;
      }

      return (data || []) as InboxGroup[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000,
  });
}

// =============================================================================
// Hook: mutations CRUD
// =============================================================================

export function useManageInboxGroup() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();

  return useMutation({
    mutationFn: async (payload: ManageInboxGroupPayload) => {
      const { data, error } = await supabase.rpc('manage_inbox_group', {
        p_action: payload.action,
        p_group_id: payload.groupId ?? null,
        p_name: payload.name ?? null,
        p_description: payload.description ?? null,
        p_color: payload.color ?? null,
        p_is_active: payload.isActive ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('useManageInboxGroup', 'Erro na operação', {
          action: payload.action,
          error: error.message,
        });
        throw error;
      }

      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-groups'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-inbox-group'] });
      // Invalida o cache de inboxes pois o group_id pode ter mudado em cascata (delete -> SET NULL)
      queryClient.invalidateQueries({ queryKey: ['crm-inboxes-filter'] });
    },
  });
}

// =============================================================================
// Hook: vincular/desvincular inbox de grupo
// =============================================================================

export function useSetInboxGroup() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();

  return useMutation({
    mutationFn: async ({ inboxId, groupId }: { inboxId: string; groupId: string | null }) => {
      const { error } = await supabase.rpc('set_inbox_group', {
        p_inbox_id: inboxId,
        p_group_id: groupId,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('useSetInboxGroup', 'Erro ao vincular grupo', {
          inboxId,
          groupId,
          error: error.message,
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox-groups'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-inbox-group'] });
      queryClient.invalidateQueries({ queryKey: ['crm-inboxes-filter'] });
    },
  });
}

// =============================================================================
// Hook: obtém o group_id atual de uma inbox (via tabela inboxes — leitura direta)
// =============================================================================

export function useInboxGroupId(inboxId: string | undefined) {
  return useQuery({
    queryKey: ['tenant-inbox-group', inboxId],
    queryFn: async (): Promise<string | null> => {
      if (!inboxId) return null;
      const { data, error } = await supabase
        .from('inboxes')
        .select('group_id')
        .eq('id', inboxId)
        .maybeSingle();

      if (error) {
        logger.error('useInboxGroupId', 'Erro ao buscar grupo da inbox', { error: error.message });
        return null;
      }

      return (data?.group_id as string | null) ?? null;
    },
    enabled: !!inboxId,
    staleTime: 60 * 1000,
  });
}

// =============================================================================
// Hook: resolve grupo -> lista de IDs de inboxes vinculadas
// =============================================================================

export function useInboxIdsByGroup(groupId: string | null | undefined) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['inbox-ids-by-group', currentTenant?.id, groupId ?? null],
    queryFn: async (): Promise<string[]> => {
      if (!currentTenant?.id || !groupId) return [];

      const { data, error } = await supabase
        .from('inboxes')
        .select('id')
        .eq('tenant_id', currentTenant.id)
        .eq('group_id', groupId);

      if (error) {
        logger.error('useInboxIdsByGroup', 'Erro ao buscar inboxes do grupo', {
          error: error.message,
        });
        return [];
      }

      return (data || []).map((r) => r.id as string);
    },
    enabled: !!currentTenant?.id && !!groupId,
    staleTime: 60 * 1000,
  });
}
